'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { CACHE_CONTROL_HEADERS, DEFAULT_CONTACT_INFO } from '@/lib/constants';
import ReCaptchaEnterprise, { resetEnterpriseCaptcha } from '@/components/ReCaptchaEnterprise';
import { motion } from 'framer-motion';
import { FaBuilding, FaPhone, FaEnvelope, FaClock, FaCreditCard, FaMapMarkerAlt } from 'react-icons/fa';
import { getApiUrl, getCacheHeaders } from '@/lib/site-config';
import { RECAPTCHA_CONFIG } from '@/config/recaptcha';

interface ContactInfo {
  company_name: string;
  address_line1: string;
  address_line2: string;
  phone1: string;
  phone2: string;
  email: string;
  opening_hours: string;
  bank_info: string;
}

// Domyślna treść RODO
const DEFAULT_RODO = {
  title: "RODO – Klauzula informacyjna",
  content: `<h3>Ośrodek Szkolenia Morskiego LIBRA</h3>
      
<p>Zgodnie z art. 13 Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. dotyczącego ochrony danych osobowych (Dz. Urz. UE L 119 z dnia 04.05.2016 r.), informujemy:</p>

<ol>
  <li>Administratorem Państwa danych osobowych jest Ośrodek Szkolenia Morskiego LIBRA, z siedzibą przy ul. Portowej 37, 78-100 Kołobrzeg.</li>
  
  <li>Kontakt z administratorem jest możliwy telefonicznie pod numerem 94 355 47 45, telefonem komórkowym 600 909 200 oraz drogą elektroniczną na adres e-mail: <a href="mailto:info@szkoleniamorskie.pl">info@szkoleniamorskie.pl</a>.</li>
  
  <li>Państwa dane osobowe będą przetwarzane wyłącznie w celu zgłoszenia chęci uczestnictwa w kursie oraz zawarcia umowy na podstawie art. 6 ust. 1 lit. b RODO.</li>
  
  <li>Dostęp do Państwa danych osobowych mogą mieć jedynie podmioty upoważnione na podstawie obowiązujących przepisów prawa.</li>
  
  <li>Okres przechowywania danych osobowych jest ograniczony do czasu niezbędnego do realizacji umowy zawartej z administratorem.</li>
  
  <li>Mają Państwo prawo do wglądu w swoje dane osobowe, ich poprawiania, żądania ich usunięcia lub ograniczenia przetwarzania, przeniesienia danych oraz wycofania zgody na ich przetwarzanie.</li>
  
  <li>W przypadku stwierdzenia, że przetwarzanie danych osobowych narusza przepisy RODO, przysługuje Państwu prawo do wniesienia skargi do organu nadzorczego.</li>
</ol>

<p>Zgodnie z art. 6 ust. 1 lit. a Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 mają Państwo możliwość dobrowolnego wyrażenia zgody na przetwarzanie danych osobowych przez Ośrodek Szkolenia Morskiego LIBRA.</p>

<p>Podanie danych osobowych jest dobrowolne, lecz niezbędne do zgłoszenia chęci uczestnictwa w kursie oraz zawarcia i realizacji umowy. Brak zgody na przekazanie danych skutkuje niemożnością zawarcia umowy.</p>`
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    rodoConsent: false
  });
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [showRodoDetails, setShowRodoDetails] = useState(false);
  const [rodoContent, setRodoContent] = useState<{ title: string, content: string }>(DEFAULT_RODO);
  const [contactInfo, setContactInfo] = useState<ContactInfo>(DEFAULT_CONTACT_INFO);
  const [bannerImage, setBannerImage] = useState<string>('/images/front01.webp');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const timestamp = Date.now();
        const baseUrl = getApiUrl('contactInfo');
        const separator = baseUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${baseUrl}${separator}t=${timestamp}`, {
          method: 'GET',
          headers: {
            ...getCacheHeaders()
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.success && data.contactInfo) {
            setContactInfo(data.contactInfo);
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania informacji kontaktowych:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRodoData = async () => {
      try {
        const timestamp = Date.now();
        const baseUrl = getApiUrl('rodo');
        const separator = baseUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${baseUrl}${separator}t=${timestamp}`, {
          method: 'GET',
          headers: {
            ...getCacheHeaders()
          }
        });

        if (response.ok) {
          const data = await response.json();
          // API zwraca data.data jako tablicę sekcji RODO - łączymy wszystkie sekcje
          if (data && data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
            const combinedContent = data.data.map((section: { title: string; content: string }) =>
              `<h4>${section.title}</h4>\n${section.content}`
            ).join('\n\n');
            setRodoContent({
              title: "RODO – Klauzula informacyjna",
              content: combinedContent
            });
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania danych RODO:', error);
      }
    };

    fetchContactInfo();
    fetchRodoData();
  }, []);

  // Efekt wybierający losowy obraz przy każdym załadowaniu strony
  useEffect(() => {
    const bannerImages = [
      '/images/front01.webp',
      '/images/front02.webp',
      '/images/front03.webp'
    ];
    const randomIndex = Math.floor(Math.random() * bannerImages.length);
    setBannerImage(bannerImages[randomIndex]);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Walidacja zgodny RODO i reCAPTCHA
    const newErrors: {[key: string]: string} = {};
    if (!formData.rodoConsent) {
      newErrors.rodoConsent = "Potwierdzenie zapoznania się z klauzulą informacyjną RODO jest wymagane";
    }
    
    if (!recaptchaToken) {
      newErrors.recaptcha = "Proszę zweryfikować, że nie jesteś robotem";
    }
    
    // Walidacja pól formularza
    if (!formData.name.trim()) {
      newErrors.name = "Imię i nazwisko jest wymagane";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Adres email jest wymagany";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Podaj prawidłowy adres email";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Temat jest wymagany";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Wiadomość jest wymagana";
    }
    
    // Jeśli są błędy, zatrzymaj proces
    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await fetch(getApiUrl('contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCacheHeaders()
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Wiadomość została wysłana pomyślnie!'
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          rodoConsent: false
        });
        setRecaptchaToken(null);
        resetEnterpriseCaptcha();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Wystąpił błąd podczas wysyłania wiadomości'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Wystąpił błąd podczas wysyłania wiadomości'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Wyczyść błąd po zmianie wartości pola
    if (formErrors[name]) {
      setFormErrors(prev => {
        const errors = {...prev};
        delete errors[name];
        return errors;
      });
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    // Wyczyść błąd po weryfikacji CAPTCHA
    if (formErrors.recaptcha) {
      setFormErrors(prev => {
        const errors = {...prev};
        delete errors.recaptcha;
        return errors;
      });
    }
  };

  return (
    <>
      <div className="min-h-screen bg-blue-50">
        {/* Hero Section */}
        <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0 bg-black">
            <div className="w-full h-full">
              <Image
                src={bannerImage}
                alt="Tło strony kontaktowej"
                fill
                priority
                quality={85}
                className="object-cover brightness-50"
                sizes="100vw"
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJSEkLzYvLy02LjY2OjY2Njo2NjY2NjY2NjY2NjY2NjY2NjY2NjY2Njb/2wBDARUXFyAeIB4gHh4gIB4lICAgICUmJSAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICb/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
              />
            </div>
          </div>
          <div className="relative z-10 text-center text-white px-4">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Kontakt
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto"
            >
              Napisz do nas w sprawie szkoleń. Zapraszamy!
            </motion.p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-full">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-lg shadow-md p-8 mb-6 hover:shadow-lg transition-shadow h-full flex flex-col"
              >
                <h2 className="text-2xl font-bold text-[rgb(6,40,95)] mb-6 border-b pb-3">Dane kontaktowe</h2>
                
                <div className="flex-grow">
                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <FaBuilding className="text-[rgb(6,40,95)] text-xl mr-3" />
                      <div className="font-bold text-gray-700">Adres</div>
                    </div>
                    <p className="text-gray-600 pl-8">
                      {contactInfo.company_name}<br />
                      {contactInfo.address_line1}<br />
                      {contactInfo.address_line2}
                    </p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <FaPhone className="text-[rgb(6,40,95)] text-xl mr-3" />
                      <div className="font-bold text-gray-700">Telefon</div>
                    </div>
                    <p className="text-gray-600 pl-8">
                      {contactInfo.phone1}<br />
                      {contactInfo.phone2}
                    </p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <FaEnvelope className="text-[rgb(6,40,95)] text-xl mr-3" />
                      <div className="font-bold text-gray-700">Email</div>
                    </div>
                    <p className="pl-8">
                      <a href={`mailto:${contactInfo.email}`} className="text-[rgb(6,40,95)] hover:underline transition-colors whitespace-nowrap overflow-hidden text-overflow-ellipsis inline-block max-w-full">{contactInfo.email}</a>
                    </p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <FaClock className="text-[rgb(6,40,95)] text-xl mr-3" />
                      <div className="font-bold text-gray-700">Godziny otwarcia</div>
                    </div>
                    <p className="text-gray-600 pl-8">
                      {contactInfo.opening_hours.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}<br />
                        </span>
                      ))}
                    </p>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center mb-3">
                      <FaCreditCard className="text-[rgb(6,40,95)] text-xl mr-3" />
                      <div className="font-bold text-gray-700">Dane bankowe</div>
                    </div>
                    <p className="text-gray-600 pl-8">
                      {contactInfo.bank_info.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}<br />
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="h-full">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white rounded-lg shadow-md p-8 mb-6 hover:shadow-lg transition-shadow h-full flex flex-col"
              >
                <h2 className="text-2xl font-bold text-[rgb(6,40,95)] mb-6 border-b pb-3">Formularz kontaktowy</h2>
                <div className="flex-grow">
                  {status.type === 'success' ? (
                    <div className="bg-green-100 text-green-700 p-6 rounded-lg border border-green-200 flex flex-col items-center justify-center h-full">
                      <svg className="w-12 h-12 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <p className="text-lg font-medium">{status.message}</p>
                      <p className="mt-3 text-gray-600">Dziękujemy za kontakt. Odpowiemy najszybciej jak to możliwe.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5 h-full flex flex-col">
                      <div className="flex-grow space-y-5">
                        <div>
                          <label htmlFor="name" className="block text-gray-700 mb-2 font-medium">Imię i nazwisko*</label>
                          <input 
                            type="text" 
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-transparent transition-all duration-200 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`} 
                            id="name" 
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            autoComplete="name"
                            required 
                            placeholder="Wpisz swoje imię i nazwisko"
                          />
                          {formErrors.name && <div className="text-red-500 mt-1 text-sm">{formErrors.name}</div>}
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-gray-700 mb-2 font-medium">Email*</label>
                          <input 
                            type="email" 
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-transparent transition-all duration-200 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`} 
                            id="email" 
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                            required 
                            placeholder="Wpisz swój adres email"
                          />
                          {formErrors.email && <div className="text-red-500 mt-1 text-sm">{formErrors.email}</div>}
                        </div>
                        <div>
                          <label htmlFor="subject" className="block text-gray-700 mb-2 font-medium">Temat*</label>
                          <input 
                            type="text" 
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-transparent transition-all duration-200 ${formErrors.subject ? 'border-red-500' : 'border-gray-300'}`} 
                            id="subject" 
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            autoComplete="off"
                            required 
                            placeholder="Wpisz temat wiadomości"
                          />
                          {formErrors.subject && <div className="text-red-500 mt-1 text-sm">{formErrors.subject}</div>}
                        </div>
                        <div>
                          <label htmlFor="message" className="block text-gray-700 mb-2 font-medium">Wiadomość*</label>
                          <textarea 
                            className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-transparent transition-all duration-200 ${formErrors.message ? 'border-red-500' : 'border-gray-300'}`} 
                            id="message" 
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            autoComplete="off"
                            rows={4} 
                            required
                            placeholder="Wpisz treść wiadomości"
                          ></textarea>
                          {formErrors.message && <div className="text-red-500 mt-1 text-sm">{formErrors.message}</div>}
                        </div>
                      </div>
                      
                      <div className="space-y-5">
                        <div>
                          {RECAPTCHA_CONFIG.SITE_KEY && (
                          <ReCaptchaEnterprise
                            sitekey={RECAPTCHA_CONFIG.SITE_KEY}
                            action="SUBMIT"
                            onChange={handleRecaptchaChange}
                          />
                          )}
                          {formErrors.recaptcha && <div className="text-red-500 mt-2 text-sm">{formErrors.recaptcha}</div>}
                        </div>
                        <div className="flex items-start space-x-3">
                          <input 
                            type="checkbox" 
                            className={`mt-1 h-5 w-5 rounded border-2 text-[rgb(6,40,95)] focus:ring-2 focus:ring-[rgb(6,40,95)] focus:ring-offset-2 transition-colors ${formErrors.rodoConsent ? 'border-red-500' : 'border-gray-400'}`} 
                            id="rodoConsent" 
                            name="rodoConsent"
                            checked={formData.rodoConsent}
                            onChange={handleChange}
                            autoComplete="off"
                            required 
                          />
                          <label className="text-gray-700 text-sm leading-relaxed cursor-pointer" htmlFor="rodoConsent">
                            Potwierdzam, że zapoznałem/am się z klauzulą informacyjną dotyczącą przetwarzania moich danych osobowych przez Ośrodek Szkolenia Morskiego LIBRA
                            <button
                              type="button"
                              className="ml-1 text-[rgb(6,40,95)] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[rgb(6,40,95)] rounded"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowRodoDetails(true);
                              }}
                            >
                              (zobacz pełną treść)
                            </button>
                            .
                            <span className="block mt-1 text-xs text-gray-500">
                              Dane podane w formularzu przetwarzane są w celu udzielenia odpowiedzi na zapytanie na podstawie art. 6 ust. 1 lit. f RODO (uzasadniony interes Administratora). Pełna informacja w <a href="/rodo" target="_blank" rel="noopener noreferrer" className="underline text-[rgb(6,40,95)]">Polityce Prywatności</a>.
                            </span>
                          </label>
                        </div>
                        {formErrors.rodoConsent && <div className="text-red-500 text-sm">{formErrors.rodoConsent}</div>}
                        {status.type === 'error' && (
                          <div className="bg-red-100 text-red-700 p-4 rounded-lg border border-red-200 mb-4" role="alert">
                            <div className="flex">
                              <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                              <span>{status.message}</span>
                            </div>
                          </div>
                        )}
                        <button 
                          type="submit" 
                          className="w-full bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/80 text-white px-6 py-3 rounded-lg transition-colors font-medium flex items-center justify-center"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Wysyłanie...
                            </>
                          ) : 'Wyślij wiadomość'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-lg shadow-md p-8 mb-6 hover:shadow-lg transition-shadow mt-12"
          >
            <div className="flex items-center mb-6">
              <FaMapMarkerAlt className="text-[rgb(6,40,95)] text-xl mr-3" />
              <h2 className="text-2xl font-bold text-[rgb(6,40,95)]">Znajdź nas</h2>
            </div>
            <div className="h-96 w-full rounded-lg overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2334.902601201345!2d15.557035212649577!3d54.18180077242885!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470015a2efb7ef91%3A0x8b222f6fc9ab5113!2sOśrodek%20Szkolenia%20Morskiego%20LIBRA!5e0!3m2!1spl!2spl!4v1750762563088!5m2!1spl!2spl"
                width="100%" 
                height="100%" 
                style={{border:0}} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              >
              </iframe>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal RODO */}
      {showRodoDetails && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-[rgb(6,40,95)]">{rodoContent.title}</h3>
              <button 
                type="button" 
                className="text-gray-400 hover:text-gray-500 transition-colors"
                onClick={() => setShowRodoDetails(false)}
                aria-label="Zamknij okno klauzuli RODO"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: rodoContent.content }}></div>
            </div>
            <div className="p-6 border-t flex justify-between items-center">
              <button 
                type="button" 
                className="bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/80 text-white px-6 py-2 rounded-lg transition-colors"
                onClick={() => setShowRodoDetails(false)}
              >
                Zamknij
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
} 